import { useEffect, useRef } from 'react';
import { useModal } from '../../contexts/ModalContext';

interface PageHeroProps {
  eyebrow?: string;
  title: string;
  subtitle: string;
  primaryCta?: string;
  secondaryCta?: string;
  secondaryHref?: string;
  bgImage: string;
}

export default function PageHero({
  eyebrow,
  title,
  subtitle,
  primaryCta = 'Start Your Project',
  secondaryCta,
  secondaryHref = '#',
  bgImage,
}: PageHeroProps) {
  const { openModal } = useModal();
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (bgRef.current) {
        bgRef.current.style.transform = `translateY(${window.scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative h-[90vh] min-h-[640px] flex items-end pb-28 overflow-hidden">
      {/* Parallax background */}
      <div
        ref={bgRef}
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url(${bgImage})`, top: '-15%', height: '130%' }}
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-ink/85 via-dark-ink/35 to-dark-ink/15" />
      <div className="absolute inset-0 bg-gradient-to-r from-dark-ink/30 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-16">
        <div className="max-w-2xl">
          {eyebrow && (
            <div className="flex items-center gap-4 mb-6">
              <div className="w-6 h-px bg-bronze" />
              <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-white/55">{eyebrow}</p>
            </div>
          )}
          <h1 className="font-serif text-5xl lg:text-7xl font-light text-white leading-[1.05] mb-6"
            style={{ animation: 'heroFadeUp 1s cubic-bezier(0.22,1,0.36,1) 0.2s both' }}>
            {title}
          </h1>
          <p className="font-sans text-base text-white/60 leading-relaxed mb-10 max-w-md"
            style={{ animation: 'heroFadeUp 1s cubic-bezier(0.22,1,0.36,1) 0.45s both' }}>
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4"
            style={{ animation: 'heroFadeUp 1s cubic-bezier(0.22,1,0.36,1) 0.65s both' }}>
            <button
              onClick={openModal}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-10 py-4 bg-white text-dark-ink hover:bg-soft-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              {primaryCta}
            </button>
            {secondaryCta && (
              <a
                href={secondaryHref}
                className="font-sans text-[10px] tracking-[0.2em] uppercase px-10 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors duration-300 cursor-pointer whitespace-nowrap text-center"
              >
                {secondaryCta}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 right-16 flex items-center gap-3" style={{ animation: 'heroFadeUp 1s ease 1.2s both' }}>
        <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/35">Scroll</span>
        <div className="w-10 h-px bg-white/20" />
      </div>

      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
